import { createContext, useContext, useEffect, useState } from "react";
import { ReadOnlyClaim } from "../../../../redux/apiSpecifications/apiClaims";
import { useClaims } from "../../../../hooks/useClaims";
import {
  Guarantee,
  PolicySubject,
  PolicySubjectRoleAction,
} from "../../../../redux/apiSpecifications/apiPolicies";
import { usePolicy } from "../../../../hooks/usePolicy";
import { Subject } from "../../../../redux/apiSpecifications/apiSubjects";
import { useSubjects } from "../../../../hooks/useSubjects";

export interface ClaimDetailsContextData {
  claim?: ReadOnlyClaim;
  status?: "idle" | "loading" | "refreshing" | "saving" | undefined;
  openSearchSubject: boolean;
  setOpenSearchSubject: (value: boolean) => void;
  onRefreshClaim: () => Promise<ReadOnlyClaim>;
  onSavePolicyGuaranteePromise: (
    policyGuarantee: Guarantee
  ) => Promise<Guarantee[]>;
  onDeletePolicyGuaranteePromise: (guaranteeId: number) => Promise<Guarantee[]>;
  onAddPolicySubjectPromise: (
    addPolicySubjectData: Subject,
    policySubjecRoleCodes: string[]
  ) => Promise<PolicySubject[]>;
  onDeletePolicySubjectPromise: (
    subjectId: number,
    subjectRoleCode: string
  ) => Promise<PolicySubject[]>;
}

type ClaimDetailsState = {
  claim?: ReadOnlyClaim;
  status?: "idle" | "loading" | "refreshing" | "saving" | undefined;
};

const ClaimDetailsContext = createContext<ClaimDetailsContextData | undefined>(
  undefined
);

interface IClaimDetailsProviderProps {
  // onCreateClaim: (createClaimData: CreateClaimData, complete: boolean) => void;
  idClaim: number;
  children: React.ReactNode;
}

const ClaimDetailsProvider: React.FC<IClaimDetailsProviderProps> = ({
  idClaim,
  children,
}) => {
  const [state, setState] = useState<ClaimDetailsState>({});

  const [openSearchSubject, setOpenSearchSubject] = useState<boolean>(false);

  const { getClaimPromise } = useClaims();

  const {
    addPolicyGuaranteePromise,
    updatePolicyGuaranteePromise,
    deletePolicyGuaranteePromise,
    setRolesToPolicySubjectsPromise,
  } = usePolicy();

  const { insertSubjectPromise } = useSubjects();

  useEffect(() => {
    (async () => {
      setState({
        ...state,
        status: "loading",
      });

      getClaimPromise(idClaim)
        .then((claim) => {
          setState({
            ...state,
            claim: claim,
            status: "idle",
          });
        })
        .catch((error) => {
          console.error(error);

          setState({
            ...state,
            claim: undefined,
            status: "idle",
          });
        });
    })();
  }, [idClaim]);

  const onRefreshClaim = (): Promise<ReadOnlyClaim> =>
    new Promise(async (resolve, reject) => {
      setState({
        ...state,
        status: "refreshing",
      });

      getClaimPromise(state.claim?.id!)
        .then((claim) => {
          setState({
            ...state,
            claim: claim,
            status: "idle",
          });
        })
        .catch((error) => {
          console.error(error);

          setState({
            ...state,
            claim: undefined,
            status: "idle",
          });

          reject(error);
        });
    });

  const onSavePolicyGuaranteePromise = (
    policyGuarantee: Guarantee
  ): Promise<Guarantee[]> =>
    new Promise(async (resolve, reject) => {
      setState({
        ...state,
        status: "saving",
      });

      if (policyGuarantee!.id) {
        updatePolicyGuaranteePromise(state.claim?.policy?.id!, policyGuarantee)
          .then((policyGuarantees) => {
            setState({
              ...state,
              claim: {
                ...state!.claim!,
                policy: {
                  ...state!.claim?.policy!,
                  guarantees: policyGuarantees,
                },
              },
              status: "idle",
            });

            resolve(policyGuarantees);
          })
          .catch((reason) => {
            setState({
              ...state,
              status: "idle",
            });

            reject(reason);
          });
      } else {
        addPolicyGuaranteePromise(state.claim?.policy?.id!, policyGuarantee)
          .then((policyGuarantees) => {
            setState({
              ...state,
              claim: {
                ...state!.claim!,
                policy: {
                  ...state!.claim?.policy!,
                  guarantees: policyGuarantees,
                },
              },
              status: "idle",
            });

            resolve(policyGuarantees);
          })
          .catch((reason) => {
            setState({
              ...state,
              status: "idle",
            });

            reject(reason);
          });
      }
    });

  const onDeletePolicyGuaranteePromise = (
    guaranteeId: number
  ): Promise<Guarantee[]> =>
    new Promise(async (resolve, reject) => {
      setState({
        ...state,
        status: "saving",
      });

      deletePolicyGuaranteePromise(state.claim?.policy?.id!, guaranteeId)
        .then((policyGuarantees) => {
          setState({
            ...state,
            claim: {
              ...state!.claim!,
              policy: {
                ...state!.claim?.policy!,
                guarantees: policyGuarantees,
              },
            },
            status: "idle",
          });

          resolve(policyGuarantees);
        })
        .catch((reason) => {
          setState({
            ...state,
            status: "idle",
          });

          reject(reason);
        });
    });

  const onAddPolicySubjectPromise = (
    addPolicySubjectData: Subject,
    policySubjecRoleCodes: string[]
  ): Promise<PolicySubject[]> =>
    new Promise(async (resolve, reject) => {
      setState({
        ...state,
        status: "saving",
      });

      if (addPolicySubjectData.id) {
        const policySubjectRoleActions: PolicySubjectRoleAction[] = [];

        policySubjecRoleCodes.forEach((roleCode) => {
          policySubjectRoleActions.push({
            subjectId: addPolicySubjectData.id,
            roleCode: roleCode,
            action: "I",
          });
        });

        setRolesToPolicySubjectsPromise(
          state.claim?.policy?.id!,
          policySubjectRoleActions
        )
          .then((policySubjects) => {
            setState({
              ...state,
              claim: {
                ...state!.claim!,
                policy: {
                  ...state!.claim?.policy!,
                  policySubjects: policySubjects,
                },
              },
              status: "idle",
            });

            resolve(policySubjects);
          })
          .catch((reason) => {
            setState({
              ...state,
              status: "idle",
            });

            reject(reason);
          });
      } else {
        insertSubjectPromise(addPolicySubjectData)
          .then((subject) => {
            const policySubjectRoleActions: PolicySubjectRoleAction[] = [];

            policySubjecRoleCodes.forEach((roleCode) => {
              policySubjectRoleActions.push({
                subjectId: subject.id,
                roleCode: roleCode,
                action: "I",
              });
            });

            setRolesToPolicySubjectsPromise(
              state.claim?.policy?.id!,
              policySubjectRoleActions
            )
              .then((policySubjects) => {
                setState({
                  ...state,
                  claim: {
                    ...state!.claim!,
                    policy: {
                      ...state!.claim?.policy!,
                      policySubjects: policySubjects,
                    },
                  },
                  status: "idle",
                });

                resolve(policySubjects);
              })
              .catch((reason) => {
                setState({
                  ...state,
                  status: "idle",
                });

                reject(reason);
              });
          })
          .catch((reason) => {
            setState({
              ...state,
              status: "idle",
            });

            reject(reason);
          });
      }
    });

  const onDeletePolicySubjectPromise = (
    subjectId: number,
    subjectRoleCode: string
  ): Promise<PolicySubject[]> =>
    new Promise(async (resolve, reject) => {
      setState({
        ...state,
        status: "saving",
      });

      setRolesToPolicySubjectsPromise(state.claim?.policy?.id!, [
        {
          subjectId: subjectId,
          roleCode: subjectRoleCode,
          action: "D",
        },
      ])
        .then((policySubjects) => {
          setState({
            ...state,
            claim: {
              ...state!.claim!,
              policy: {
                ...state!.claim?.policy!,
                policySubjects: policySubjects,
              },
            },
            status: "idle",
          });

          resolve(policySubjects);
        })
        .catch((reason) => {
          setState({
            ...state,
            status: "idle",
          });

          reject(reason);
        });
    });

  return (
    <ClaimDetailsContext.Provider
      value={{
        status: state.status,
        claim: state.claim,
        openSearchSubject: openSearchSubject,
        setOpenSearchSubject,
        onRefreshClaim,
        onSavePolicyGuaranteePromise,
        onDeletePolicyGuaranteePromise,
        onAddPolicySubjectPromise,
        onDeletePolicySubjectPromise,
      }}
    >
      {children}
    </ClaimDetailsContext.Provider>
  );
};

export const useClaimDetailsContext = (): ClaimDetailsContextData => {
  const context = useContext(ClaimDetailsContext);

  if (!context) {
    throw new Error("Errore");
  }

  return context;
};

export default ClaimDetailsProvider;
